import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, Fragment } from "react";
import { css } from "emotion";
import { useNewOffer } from "../../../../context/new-offer";
import R from "ramda";
import Input from "../../../../../../offers/shared/AtomicInput";
import { stepContent, header, singleInput, quantMilesStyle, offerMilesStyle, title } from "./../style";
import { milesMask } from "../../../../../shared/create-offer/utils/masks";
import { intFormat, formatOnlyNumbers } from "../../../../../shared/create-offer/utils/formats";
import Loading from "../../../../../shared/create-offer/Loading/Loading";
import InputForm from "../../../../../shared/atoms/InputForm";
import {
	MANAGER_LIMIT_AVERAGE_MILES,
	MINIMAL_MILES_ADVANCED_AVERAGE,
	MINIMAL_MILES_SMILES_REDUCTIONS,
	AIRLINE_PROGRAMS
} from "../../../../shared/enums";
import SelectForm from "../../../../../shared/atoms/SelectForm";
import SwitchForm from "../../../../../shared/atoms/SwitchForm";
import SeeMore from "../../shared/SeeMore";
import { useNewOfferMobile } from "../../context";
import { AverageMessage } from "../../../shared/miles-average/AverageMessage";
import { LATAM_FIDELITY_ID_PROGRAM, MULTIPLE_MILES_OFFER as multipleMilesOffer } from "../../../../../../js/shared/constants";
import { Tooltip } from "react-tippy";
import theme from "../../../../../../js/shared/new-theme";
import { Icon } from "@maxmilhas/hangar-components-atomic/public";
import { formatPaymentDeadline } from "../../../../../shared/create-offer/utils/formats";
import Callout from "../../../../../shared/create-offer/Callout/Callout";

// Debounce function
let timerId;
const debounced = (fn, delay = 250) => {
	if (timerId) clearTimeout(timerId);
	timerId = setTimeout(fn, delay);
};

const QuantMiles = forwardRef(({ passData }, ref) => {
	const {
		currentProgram,
		fetchRankingData,
		participantData,
		offerData,
		rankingData,
		pricesData,
		setOfferData,
		setRankingData,
		fetchPricesData,
		setSmilesRange,
		smilesDiamond,
		setSmilesDiamond,
		statisticSales,
		fetchStatisticSales,
		bonusData: { data: bonusData },
		editOffer,
		twoFactorData
	} = useNewOffer();
	const { setStepError } = useNewOfferMobile();

	const { minMiles, maxMiles } = pricesData.data;

	const [valid, setValid] = useState(undefined);
	const [quantOfferMiles, setQuantOfferMiles] = useState(
		offerData.data.quant || editOffer.quant || ""
	);
	const [paymentDeadline, setPaymentDeadline] = useState(
		offerData.data.paymentDeadline || editOffer.paymentDeadline || ""
	);
	const [paymentScheduleType, setPaymentScheduleType] = useState(
		offerData.data.paymentScheduleType || editOffer.paymentScheduleType || ""
	);
	const quantRef = useRef(quantOfferMiles);
	const [maxAverage, setMaxAverage] = useState(
		participantData.hasManagementActive ? MANAGER_LIMIT_AVERAGE_MILES : offerData.data.averageMilesMax || 0
	);

	const [errors, setErrors] = useState({
		quantOfferMiles: quantOfferMiles && ((quantOfferMiles < minMiles || quantOfferMiles > maxMiles) || quantOfferMiles % multipleMilesOffer !== 0)
	});
	const [averageOfferMiles, setAverageOfferMiles] = useState(
		offerData.data.averageMiles || rankingData.data.averageMilesSuggestion || ""
	);

	const [averageMilesPerTicket, setAverageMilesPerTicket] = useState(
		offerData.data.averageMiles || rankingData.data.averageMilesSuggestion || ""
	);
	const [advancedSettings, setAdvancedSettings] = useState(false);
	const [reductions, setReductions] = useState(offerData.data.reductions || 0);
	const [optionsMilesReductionTickets, setOptionsMilesReductionTickets] = useState([]);
	const [optionsAverageMilesPerTicket, setOptionsAverageMilesPerTicket] = useState([]);

	const showReductions =
		currentProgram.id === AIRLINE_PROGRAMS.SMILES.id &&
		!participantData.hasManagementActive &&
		smilesDiamond === true &&
		!!optionsMilesReductionTickets.length;

	const [modals, setModals] = useState({
		mediaVendas: false,
		firstOpen: false,
		numberAverageTicket: 0,
		value: 0,
		sale: {}
	});

	const [inputSelected, setInputSelected] = useState("quantOfferMiles");

	// Pass ref to modals
	useImperativeHandle(ref, () => ({
		async confirmVendasModal() {
			setAverageMilesPerTicket(modals.value.length ? modals.numberAverageTicket : "");
			await setOfferData({
				data: { ...offerData.data, averageMiles: modals.numberAverageTicket }
			});
			await fetchPricesData(quantOfferMiles, currentProgram, modals.numberAverageTicket);
		}
	}));

	useEffect(() => {
		handleReductions(
			pricesData.data.reductionRanges,
			quantOfferMiles,
			reductions,
			averageMilesPerTicket,
			offerData.averageMilesMax
		);
	}, []);

	useEffect(() => {
		setSmilesRange(reductions && optionsAverageMilesPerTicket);
	}, [reductions, optionsAverageMilesPerTicket]);

	useEffect(() => {
		const averageMilesMax = parseInt(offerData.data.averageMilesMax);
		if (averageMilesMax > 0)
			setMaxAverage(averageMilesMax);
	}, [offerData.data.averageMilesMax]);

	useEffect(() => {
		let average = offerData.data.averageMiles || "";
		if (average && average !== averageOfferMiles) {
			if (reductions) {
				onChange("averageMilesPerTicket", String(average));
			} else {
				onChange("averageOfferMiles", average);
			}
		}
	}, [offerData.data.averageMiles]);

	const getReductions = async (isSmilesDiamond) => {
		await fetchRankingData(quantOfferMiles, currentProgram, undefined, false, async data => {
			await handleReductions(data.reductionRanges, quantOfferMiles, undefined, undefined, isSmilesDiamond, maxAverage);
		});
	};

	const toggleCheckSmiles = async value => {
		setSmilesDiamond(value);

		if (value) {
			if (optionsMilesReductionTickets.length > 0) {
				changeReduction(
					optionsMilesReductionTickets[optionsMilesReductionTickets.length - 1].quantReduction
				);
			} else {
				await getReductions(value);
			}
		} else {
			changeReduction(0);
		}
	};


	const handleReductions = async (
		reductionRanges = [],
		quantMiles,
		defaultReduction,
		averageMilesPerTicket,
		isSmilesDiamond = false,
		averageMax
	) => {
		if (reductionRanges.length) {
			let newReductions = [
				{
					label: "0 passageiro",
					value: 0
				}
			];

			let reductionsSorted = R.sortBy(R.prop("quantReduction"), reductionRanges);
			reductionsSorted.forEach(r => {
				newReductions.push({
					...r,
					label: `${r.quantReduction} passageiro${r.quantReduction > 1 ? "s" : ""}`,
					value: r.quantReduction
				});
			});
			setOptionsMilesReductionTickets(newReductions);
			// Checkbox marcado OU usuario salvo com categoria diamante
			if (
				(isSmilesDiamond || smilesDiamond) &&
				quantMiles >= MINIMAL_MILES_SMILES_REDUCTIONS &&
				(defaultReduction === undefined || defaultReduction > 0) // Caso for redução 0 não precisa setar
			) {
				return await changeReduction(
					defaultReduction || reductionsSorted[reductionsSorted.length - 1].quantReduction,
					newReductions,
					quantMiles,
					averageMilesPerTicket,
					averageMax
				);
			}
		} else {
			setOptionsMilesReductionTickets([]);
			setOptionsAverageMilesPerTicket([]);
		}

		return {};
	};

	const changeReduction = async (
		value,
		reductionRanges = optionsMilesReductionTickets,
		quant = quantOfferMiles,
		averageMilesPerTicket,
		averageMax
	) => {
		setReductions(value);
		let newReduction = reductionRanges.find(r => r.quantReduction === value);
		let newOfferData = {
			...offerData.data,
			quant
		};
		if (newReduction) {
			let newAverages = [];
			newReduction.averageRanges.forEach(a => {
				if (averageMax && averageMax > 0 && a > averageMax) return;
				
				newAverages.push({
					label: intFormat(a),
					value: a
				});
			});
			setOptionsAverageMilesPerTicket(newAverages);

			let newAverageMiles = averageMilesPerTicket
				? newReduction.averageRanges.find(av => av === averageMilesPerTicket)
				: undefined;
			newAverageMiles = newAverageMiles || newReduction.averageRanges[0];
			setAverageMilesPerTicket(newAverageMiles);

			newOfferData = {
				...newOfferData,
				reductions: value,
				averageMiles: newReduction.averageRanges[0]
			};
			await setOfferData({
				data: newOfferData
			});
			await fetchPricesData(quant, currentProgram, newReduction.averageRanges[0]);
		} else {
			let newAverageMiles = rankingData.data.averageMilesSuggestion || averageOfferMiles;
			setAverageOfferMiles(newAverageMiles);
			newOfferData = {
				...newOfferData,
				reductions: value,
				averageMiles: newAverageMiles
			};
			await setOfferData({
				data: newOfferData
			});
			await fetchPricesData(quant, currentProgram, newAverageMiles);
		}

		return newOfferData;
	};

	const onChange = async (name, value) => {
		switch (name) {
			case "milesReductionTickets":
				value = parseInt(value);
				changeReduction(value, optionsMilesReductionTickets, quantOfferMiles, undefined, maxAverage);
				break;
			case "quantOfferMiles":
				let numberQuant = value.length ? parseInt(formatOnlyNumbers(value)) : undefined;

				let quantOfferMilesIsValid = numberQuant >= minMiles && numberQuant <= maxMiles && numberQuant % multipleMilesOffer === 0;;

				quantRef.current = numberQuant;

				setQuantOfferMiles(value.length ? numberQuant : "");
				setMaxAverage(
					participantData.hasManagementActive
						? MANAGER_LIMIT_AVERAGE_MILES
						: value.length
							? numberQuant
							: 0
				);
				setAverageOfferMiles("");
				let newErrors = {
					...errors,
					quantOfferMiles: !quantOfferMilesIsValid
				};
				setErrors(newErrors);

				if (quantOfferMilesIsValid) {
					let newOfferData = { ...offerData.data, price: "", quant: numberQuant };
					await setOfferData({
						data: newOfferData
					});
					await fetchRankingData(numberQuant, currentProgram, undefined, false, async data => {
						if (quantRef.current != numberQuant) {
							return;
						}

						const averageMilesMax = parseInt(data.averageMilesMax) || numberQuant;
						if (!participantData.hasManagementActive && averageMilesMax)
							setMaxAverage(numberQuant < averageMilesMax ? numberQuant : averageMilesMax);

						newOfferData = {
							...newOfferData,
							...(await handleReductions(data.reductionRanges, numberQuant, undefined, undefined, undefined, averageMilesMax))
						};

						let averageMiles = data.averageMilesSuggestion;
						if (!averageMiles || !data.countAllowedSales) {
							setAdvancedSettings(true);
						}

						if (!smilesDiamond) {
							setAverageOfferMiles(averageMiles);
							await setOfferData({
								data: { ...newOfferData, averageMiles }
							});
							if (averageMiles >= minMiles) {
								setErrors({
									...newErrors,
									averageOfferMiles:
										averageMiles < minMiles ||
										averageMiles > numberQuant ||
										(participantData.hasManagementActive &&
											averageMiles > MANAGER_LIMIT_AVERAGE_MILES)
								});
							}
						} else if (numberQuant < 60000 && !!data.countAllowedSales) {
							// Caso usuário for diamante e esta ofertando menos que 60k
							newOfferData.reductions = numberQuant >= 35000 ? 1 : 0;
							setAverageOfferMiles(3500);
							await setOfferData({
								data: { ...newOfferData }
							});
						}
					}, paymentDeadline, paymentScheduleType);
				} else {
					setRankingData({ ...rankingData, data: {} });
					await setOfferData({
						data: { ...offerData.data, price: "" }
					});
				}
				break;
			case "averageMilesPerTicket":
				let numberAverageTicket = value.length ? parseInt(formatOnlyNumbers(value)) : undefined;
				if (
					!showReductions &&
					numberAverageTicket > 25000 &&
					!modals.firstOpen &&
					offerData.data.quant >= MINIMAL_MILES_ADVANCED_AVERAGE
				) {
					let sale = statisticSales.data.find(
						val => val.initialMiles <= numberAverageTicket && val.finalMiles >= numberAverageTicket
					);

					setModals({
						mediaVendas: true,
						firstOpen: true,
						numberAverageTicket,
						value,
						sale
					});

					passData({
						mediaVendas: true,
						firstOpen: true,
						numberAverageTicket,
						averageMilesSuggestion: rankingData.data.averageMilesSuggestion,
						value,
						sale
					});

					return;
				}
				setAverageMilesPerTicket(value.length ? numberAverageTicket : "");
				await setOfferData({
					data: { ...offerData.data, averageMiles: numberAverageTicket }
				});
				await fetchRankingData(quantOfferMiles, currentProgram, numberAverageTicket, false, null, paymentDeadline, paymentScheduleType);
				break;
			case "averageOfferMiles":
				let numberAverage = value.length ? parseInt(formatOnlyNumbers(value)) : undefined;

				let averageOfferMilesIsValid = numberAverage >= minMiles && numberAverage <= maxAverage;

				setAverageOfferMiles(value.length ? numberAverage : "");
				setErrors({
					...errors,
					averageOfferMiles: !averageOfferMilesIsValid
				});

				if (averageOfferMilesIsValid) {
					// await fetch new prices
					await setOfferData({
						data: { ...offerData.data, averageMiles: numberAverage }
					});
					await fetchRankingData(quantOfferMiles, currentProgram, numberAverage, false, null, paymentDeadline, paymentScheduleType);
					if (numberAverage > 25000) {
						await fetchStatisticSales();
					}
				}
				break;
			case "showAdvancedSettings":
				setAdvancedSettings(!advancedSettings);
				break;
		}
	};

	const getAdvancedConfigMinMiles = () =>
		bonusData && bonusData.enabled ? bonusData.minMiles : MINIMAL_MILES_ADVANCED_AVERAGE;

	const ruleOver60 =
		quantOfferMiles >= getAdvancedConfigMinMiles(currentProgram) &&
		(currentProgram.international === "0" || !currentProgram.international || currentProgram.id === AIRLINE_PROGRAMS.TAP.id);

	let newValid = quantOfferMiles && !errors.quantOfferMiles;
	let average = reductions > 0 ? averageMilesPerTicket : averageOfferMiles;
	if (ruleOver60) {
		newValid =
			newValid &&
			average &&
			average >= minMiles &&
			average <= maxAverage &&
			!errors.averageOfferMiles;
	}

	if (valid !== newValid) {
		setValid(newValid);
		setStepError(!newValid);
	}

	const selectPassengers = showReductions ? (
		<div className="miles-reduction-tickets">
			<SelectForm
				value={reductions}
				name="milesReductionTickets"
				options={optionsMilesReductionTickets.map(r => {
					return { label: r.label, value: r.value };
				})}
				onChange={ev => {
					let val = ev.target.value;
					debounced(() => onChange("milesReductionTickets", val), 300);
				}}
				defaultOption=""
				label="Passagens com redução de milhas"
				data-action="simulator-miles-reducao-select"
				onClick={ev => setInputSelected(ev.target.name)}
			/>
		</div>
	) : null;

	const inputCheckDiamond = currentProgram.id === AIRLINE_PROGRAMS.SMILES.id && !participantData.hasManagementActive && (
		<>
			<hr className="check-smiles-diamond--separator" />
			<div className="check-smiles-diamond--wrapper">
				<Input
					name="checkSmilesDiamond"
					id="checkSmilesDiamond"
					type="checkbox"
					label={
						<span className="check-smiles-diamond--label">
							Eu sou cliente da <strong>Categoria Diamante</strong>
						</span>
					}
					value={!!smilesDiamond}
					checked={!!smilesDiamond}
					onChange={() => toggleCheckSmiles(!smilesDiamond)}
				/>
			</div>
		</>
	);

	const blockAlertLatam = twoFactorData.is2FAEnabled && !twoFactorData.code && (
		<div className="alert-offer-miles">
			<Callout show warn text={<span>{twoFactorData.alert}</span>} />
		</div>
	);

	const blockQuantMiles = (
		<div className="quant-offer-miles">
			<div className="payment-deadline--wrapper">
				<InputForm
					className="payment-deadline--component"
					name="paymentDeadline"
					id="paymentDeadline"
					value={formatPaymentDeadline(paymentDeadline, paymentScheduleType)}
					disabled
					placeholder=""
					label={
						<Fragment>Prazo de pagamento 
							<Tooltip
								tabIndex={0}
								arrow={true}
								position="top"
								theme="light"
								html={
									<p
										style={{
											color: theme.palette.gray[500].value
										}}
									>
										O prazo para o pagamento começa a contar a partir das emissões das passagens.
									</p>
								}
							>
								<Icon
									name="icon-max-communication-doubt"
									size="small"
									color={theme.palette.secondary[500].value}
								/>
							</Tooltip>
						</Fragment>}
				/>
			</div>
			<div className="quant-offer-miles--wrapper">
				<InputForm
					className="quant-offer-miles--component"
					name="quantOfferMiles"
					id="quantOfferMiles"
					type="tel"
					value={intFormat(quantOfferMiles)}
					onChange={ev => onChange("quantOfferMiles", ev.target.value)}
					placeholder=""
					label="Milhas ofertadas"
					mask={milesMask}
					hasError={errors.quantOfferMiles}
					errorMessage={
						quantOfferMiles < minMiles
							? `A oferta precisa ter no mínimo ${intFormat(minMiles)} milhas`
							: quantOfferMiles > maxMiles
								? `A oferta precisa ter no máximo ${intFormat(maxMiles)} milhas`
								: `Vendas só de ${multipleMilesOffer} em ${multipleMilesOffer} milhas`
					}
					data-action="simulator-miles-quant-miles"
					onClick={ev => setInputSelected(ev.target.name)}
				/>
			</div>
			{ruleOver60 && (
				<SwitchForm
					name="showAdvancedSettings"
					id="showAdvancedSettings"
					label="Exibir configurações avançadas"
					onChange={checked => onChange("showAdvancedSettings", checked)}
					checked={advancedSettings}
					dataAction="simulator-miles-advanced-settings"
				/>
			)}
		</div>
	);

	const inputOrSelectAverageMiles =
		!showReductions || !reductions ? (
			<div className="average-offer-miles--wrapper">
				<InputForm
					className="average-offer-miles--component"
					name="averageOfferMiles"
					id="averageOfferMiles"
					type="tel"
					value={intFormat(averageOfferMiles)}
					onChange={ev => {
						let val = ev.target.value;
						debounced(() => onChange("averageOfferMiles", val), 300);
					}}
					placeholder=""
					label="Média de milhas por passageiro"
					mask={milesMask}
					disabled={rankingData.isFetching}
					hasError={errors.averageOfferMiles}
					errorMessage={
						!averageOfferMiles || averageOfferMiles < 3500
							? `A média precisa ter no mínimo ${intFormat(minMiles)} milhas`
							: `A média precisa ter no máximo ${intFormat(maxAverage)} milhas`
					}
					onClick={ev => setInputSelected(ev.target.name)}
					data-action="simulator-miles-average-miles"
				/>
			</div>
		) : (
			<div className="average-miles-per-ticket">
				<SelectForm
					name="averageMilesPerTicket"
					value={averageMilesPerTicket}
					options={optionsAverageMilesPerTicket}
					onChange={ev => {
						let val = ev.target.value;
						debounced(() => onChange("averageMilesPerTicket", val), 300);
					}}
					defaultOption="A partir de:"
					label="Média de milhas por passageiro"
					data-action="simulator-miles-average-miles"
					onClick={ev => setInputSelected(ev.target.name)}
				/>
			</div>
		);

	const blockAverageMiles = advancedSettings && ruleOver60 && (
		<>
			{inputCheckDiamond}
			{selectPassengers}
			{inputOrSelectAverageMiles}
			<div
				className={css`
					${quantMilesStyle}
				`}
			>
				<div className="info">
					{rankingData.isFetching
						? null
						: !!rankingData.data.averageMilesSuggestion &&
						(!showReductions || !reductions) && (
							<AverageMessage
								averageMilesSuggestion={rankingData.data.averageMilesSuggestion}
								averageMiles={averageOfferMiles}
								data={statisticSales.data}
								minMiles={minMiles}
								maxAverage={maxAverage}
							/>
						)}
				</div>
			</div>
		</>
	);

	const quantInfo = (
		<>
			<p>
				<strong css={css`${title}`}>Quantidade de milhas da sua oferta</strong>
			</p>
			<p>Informe quanto você quer receber por cada 1.000 milhas na MaxMilhas.</p>
			<p>
				A quantidade mínima para essa oferta é de {intFormat(minMiles || 0)} milhas, combinado?
				Lembrando que você pode ofertar o máximo de milhas que você tem no seu programa de
				fidelidade.
			</p>
			{currentProgram.international === "0" && (
				<p>
					Se sua oferta for maior do que {intFormat(getAdvancedConfigMinMiles())} milhas, você pode
					utilizar o botão Configurações Avançadas, que aparecerá na sua tela, para definir a média
					de milhas por passageiro. É esta média que vai determinar as emissões de passagens que
					serão feitas pela MaxMilhas.
				</p>
			)}
		</>
	);

	const averageInfo = (
		<>
			<strong css={css`${title}`}>O que é média de milhas?</strong>
			<p>
				É a quantidade média de milhas que poderemos utilizar nas emissões feitas através da sua
				conta para cada passageiro.
			</p>
			<p>
				Sempre que você cadastra ou edita uma oferta com quantidade de milhas maior do que 80.000,
				pode indicar a média de milhas por passageiro.
			</p>
			<p>
				Vale lembrar que a MaxMilhas pode emitir passagens com quantidade de milhas menor ou maior
				do que a média informada. No fim das vendas, a média de milhas por passageiro será igual ou
				superior à da sua oferta.
			</p>
		</>
	);

	const reductionInfo = (
		<>
			<strong css={css`${title}`}>O que são passagens com redução de milhas?</strong>
			<p>
				Cliente Smiles da categoria Diamante pode resgatar até 10 passagens por ano utilizando no
				máximo 35.000 milhas, mesmo que o voo tenha quantidade de milhas maior para emissão.
			</p>
			<p>
				Defina quantas passagens podemos emitir com este benefício de redução de milhas Diamante. Em
				cada uma delas, serão utilizadas 35.000 milhas. Quanto mais passagens você liberar, mais
				rápidas serão suas vendas.
			</p>
		</>
	);

	return (
		<div
			className={css`
				${stepContent};
			`}
		>
			<strong
				className={css`
					${header};
				`}
			>
				Oferta de milhas
			</strong>

			{blockAlertLatam}
			<div
				className={css`
					${singleInput};
				`}
			>
				<div
					className={css`
						${offerMilesStyle}
					`}
				>
					{blockQuantMiles}
					{blockAverageMiles}
					{rankingData.isFetching ? (
						<div className="info-loading">
							<Loading width={36} height={36} />
						</div>
					) : null}
				</div>
			</div>

			{inputSelected === "quantOfferMiles" ? (
				<SeeMore children={quantInfo} />
			) : inputSelected === "milesReductionTickets" ? (
				<SeeMore children={reductionInfo} />
			) : (
				<SeeMore children={averageInfo} />
			)}
		</div>
	);
});

export default QuantMiles;
